import { offlineStorage } from './offlineStorage';
import { API_BASE_URL } from './utils';

// Type declaration for Background Sync API
interface BackgroundSyncManager {
  register(tag: string): Promise<void>;
  getTags(): Promise<string[]>;
}

declare global {
  interface ServiceWorkerRegistration {
    readonly sync: BackgroundSyncManager;
  }
}

class SyncManager {
  private syncInProgress = false;
  private syncInterval: NodeJS.Timeout | null = null;

  async initialize(): Promise<void> {
    await offlineStorage.init();
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());

    // Start periodic sync if online
    if (navigator.onLine) {
      this.startPeriodicSync();
    }

    // Register background sync if supported
    if ('serviceWorker' in navigator && 'sync' in ServiceWorkerRegistration.prototype) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.sync.register('sync-data');
      } catch (error) {
        console.warn('Background sync registration failed:', error);
      }
    }
  }

  private handleOnline(): void {
    console.log('Connection restored - starting sync');
    this.startPeriodicSync();
    this.syncPendingChanges();
  }

  private handleOffline(): void {
    console.log('Connection lost - entering offline mode');
    this.stopPeriodicSync();
  }

  private startPeriodicSync(): void {
    if (this.syncInterval) return;
    
    // Sync every 30 seconds when online
    this.syncInterval = setInterval(() => {
      this.syncPendingChanges();
    }, 30000);
  }

  private stopPeriodicSync(): void {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  async syncPendingChanges(): Promise<void> {
    if (this.syncInProgress || !navigator.onLine) return;

    this.syncInProgress = true;

    try {
      const pendingChanges = await offlineStorage.getPendingSync();
      
      if (pendingChanges.length === 0) {
        this.syncInProgress = false;
        return;
      }

      console.log(`Syncing ${pendingChanges.length} pending changes...`);

      for (const change of pendingChanges) {
        try {
          await this.syncChange(change);
          await offlineStorage.deletePendingSync(change.id);
          console.log(`Synced change: ${change.type} ${change.entity} ${change.data.id}`);
        } catch (error) {
          console.error(`Failed to sync change ${change.id}:`, error);
          
          // Increment retry count
          await offlineStorage.updatePendingSyncRetry(change.id);
          
          // Remove if too many retries (more than 10)
          if (change.retryCount >= 10) {
            console.warn(`Removing change ${change.id} after 10 failed retries`);
            await offlineStorage.deletePendingSync(change.id);
          }
        }
      }

      await offlineStorage.setLastSync(Date.now());
    } finally {
      this.syncInProgress = false;
    }
  }

  private async syncChange(change: any): Promise<void> {
    const token = localStorage.getItem('token');
    if (!token) throw new Error('No authentication token');

    const endpoint = this.getEndpoint(change.entity, change.data.id);
    const method = this.getMethod(change.type);

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: change.type !== 'DELETE' ? JSON.stringify(change.data) : undefined,
    });

    if (!response.ok) {
      throw new Error(`Sync failed: ${response.status} ${response.statusText}`);
    }
  }

  private getEndpoint(entity: string, id?: string): string {
    const endpoints: Record<string, string> = {
      incident: '/incidents',
      unit: '/units',
      majorIncident: '/major-incidents',
      log: '/logs',
    };

    const base = endpoints[entity] || '/data';
    return id ? `${base}/${id}` : base;
  }

  private getMethod(type: string): string {
    switch (type) {
      case 'CREATE': return 'POST';
      case 'UPDATE': return 'PUT';
      case 'DELETE': return 'DELETE';
      default: return 'POST';
    }
  }

  async downloadDataForOffline(token: string, eventId?: string | null): Promise<void> {
    if (!navigator.onLine) {
      console.warn('Cannot download data while offline');
      return;
    }

    try {
      console.log('Downloading data for offline use...');

      // Fetch incidents
      const incidentsUrl = eventId 
        ? `${API_BASE_URL}/incidents?eventId=${eventId}`
        : `${API_BASE_URL}/incidents`;
      const incidentsRes = await fetch(incidentsUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (incidentsRes.ok) {
        const incidentsData = await incidentsRes.json();
        const incidents = incidentsData.incidents || incidentsData.data || [];
        await offlineStorage.bulkSaveIncidents(incidents);
        console.log(`Saved ${incidents.length} incidents for offline use`);
      }

      // Fetch units
      const unitsRes = await fetch(`${API_BASE_URL}/units`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (unitsRes.ok) {
        const unitsData = await unitsRes.json();
        const units = unitsData.units || unitsData.data || [];
        await offlineStorage.bulkSaveUnits(units);
        console.log(`Saved ${units.length} units for offline use`);
      }

      // Fetch major incidents
      const majorIncidentsUrl = eventId
        ? `${API_BASE_URL}/major-incidents?eventId=${eventId}`
        : `${API_BASE_URL}/major-incidents`;
      const majorIncidentsRes = await fetch(majorIncidentsUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (majorIncidentsRes.ok) {
        const majorIncidentsData = await majorIncidentsRes.json();
        const majorIncidents = majorIncidentsData.majorIncidents || majorIncidentsData.data || [];
        await offlineStorage.bulkSaveMajorIncidents(majorIncidents);
        console.log(`Saved ${majorIncidents.length} major incidents for offline use`);
      }

      await offlineStorage.setLastSync(Date.now());
      console.log('Offline data download complete');
    } catch (error) {
      console.error('Failed to download offline data:', error);
      throw error;
    }
  }

  async queueChange(type: 'CREATE' | 'UPDATE' | 'DELETE', entity: 'incident' | 'unit' | 'majorIncident' | 'log', data: any): Promise<void> {
    await offlineStorage.addPendingSync({ type, entity, data });
    
    // Try to sync immediately if online
    if (navigator.onLine) {
      this.syncPendingChanges();
    }
  }

  isOnline(): boolean {
    return navigator.onLine;
  }

  async getPendingSyncCount(): Promise<number> {
    const pending = await offlineStorage.getPendingSync();
    return pending.length;
  }

  async getLastSyncTime(): Promise<number | null> {
    return offlineStorage.getLastSync();
  }
}

export const syncManager = new SyncManager();
